// frontend/src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import LandingPage from './components/LandingPage';
import TeamPage from './components/TeamPage';
import AdminDashboard from './components/AdminDashboard';
import AdminTeamDetail from './components/AdminTeamDetail';
import AdminEvents from './components/AdminEvents';
import AdminPlayerDirectory from './components/AdminPlayerDirectory';
import AdminIncompletePlayers from './components/AdminIncompletePlayers';
import AdminSettings from './components/AdminSettings';
import AdminTeamManagers from './components/AdminTeamManagers';
import TeamLogin from './components/TeamLogin';
import TeamManagerRoute from './components/TeamManagerRoute';
import TeamRosterPage from './components/TeamRosterPage';
import Login from './components/Login';
import AccessError from './components/AccessError';
import { teamService } from './services/supabaseClient';
import { AuthProvider, useAuth } from './contexts/AuthProvider';

// Reusable 404 page (used both by the catch-all route and ProtectedRoute when
// an authenticated non-admin user lands on an /admin/* URL).
const NotFoundPage = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Page Not Found</h1>
      <p className="text-gray-600 mb-4">The page you're looking for doesn't exist.</p>
      <a href="/" className="text-blue-600 hover:text-blue-800 underline">
        Go to Home
      </a>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, role, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin shells require an admin, super_admin, or event_admin role. Authenticated
  // users without one of those (e.g., a team manager who signed in via magic link and
  // tries to type an /admin/* URL) get a 404 instead of an empty admin page.
  if (role !== 'admin' && role !== 'super_admin' && role !== 'event_admin') {
    return <NotFoundPage />;
  }

  if (requiredRole === 'super_admin' && role !== 'super_admin') {
    // Super-admin-only pages (settings, team managers): send others back to Events.
    return <Navigate to="/admin/events" replace />;
  }

  if (requiredRole === 'admin' && role === 'event_admin') {
    // Full-access-admin pages (e.g. the cross-event Players directory) are not for
    // event_admins, who are scoped to their assigned events.
    return <Navigate to="/admin/events" replace />;
  }

  return children;
};

// Guards event-specific admin routes: an event_admin may only enter events they've
// been assigned. Must render inside a Route so useParams() sees :eventCode.
const EventScopedRoute = ({ children }) => {
  const { role, assignedEventCodes } = useAuth();
  const { eventCode } = useParams();

  if (role === 'event_admin') {
    const allowed = assignedEventCodes.includes((eventCode || '').toLowerCase());
    if (!allowed) {
      return <NotFoundPage />;
    }
  }

  return children;
};

// Wrapper component for team pages with dynamic validation
const TeamPageWrapper = () => {
  const { eventCode, teamCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [teamName, setTeamName] = useState(null);
  const [resolvedEventName, setResolvedEventName] = useState(null);

  useEffect(() => {
    const validate = async () => {
      setLoading(true);
      const { isValid, teamName, eventName } = await teamService.validateAccess(eventCode, teamCode);

      if (isValid) {
        setValid(true);
        setTeamName(teamName);
        setResolvedEventName(eventName);
      } else {
        setValid(false);
      }
      setLoading(false);
    };

    validate();
  }, [eventCode, teamCode]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // If invalid access code, show error
  if (!valid) {
    return <AccessError type="invalid" />;
  }

  // Valid access - render team page with actual team name and resolved event name
  return <TeamPage eventCode={eventCode} teamName={teamName} eventDbName={resolvedEventName} />;
};

// Admin wrapper components
const AdminDashboardWrapper = () => <AdminDashboard eventCode={useParams().eventCode} />;

const AdminTeamDetailWrapper = () => {
  const { eventCode, teamName } = useParams();
  const decodedTeamName = decodeURIComponent(teamName);
  return <AdminTeamDetail eventCode={eventCode} teamName={decodedTeamName} />;
};

const AdminIncompletePlayersWrapper = () => <AdminIncompletePlayers eventCode={useParams().eventCode} />;

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Landing page */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />

          {/* Admin routes (Protected) */}
          <Route path="/admin/events" element={
            <ProtectedRoute>
              <AdminEvents />
            </ProtectedRoute>
          } />

          <Route path="/admin/players" element={
            <ProtectedRoute requiredRole="admin">
              <AdminPlayerDirectory />
            </ProtectedRoute>
          } />

          <Route path="/admin/settings" element={
            <ProtectedRoute requiredRole="super_admin">
              <AdminSettings />
            </ProtectedRoute>
          } />

          <Route path="/admin/team-managers" element={
            <ProtectedRoute requiredRole="super_admin">
              <AdminTeamManagers />
            </ProtectedRoute>
          } />

          <Route path="/admin/:eventCode" element={
            <ProtectedRoute>
              <EventScopedRoute>
                <AdminDashboardWrapper />
              </EventScopedRoute>
            </ProtectedRoute>
          } />

          {/* Static segment outranks the :teamName route below */}
          <Route path="/admin/:eventCode/incomplete" element={
            <ProtectedRoute>
              <EventScopedRoute>
                <AdminIncompletePlayersWrapper />
              </EventScopedRoute>
            </ProtectedRoute>
          } />

          <Route path="/admin/:eventCode/:teamName" element={
            <ProtectedRoute>
              <EventScopedRoute>
                <AdminTeamDetailWrapper />
              </EventScopedRoute>
            </ProtectedRoute>
          } />

          {/* Direct admin route for convenience */}
          <Route path="/admin" element={<Navigate to="/admin/events" replace />} />

          {/* Team Manager (magic-link) routes */}
          <Route path="/team/login" element={<TeamLogin />} />
          <Route path="/team/roster" element={
            <TeamManagerRoute>
              <TeamRosterPage />
            </TeamManagerRoute>
          } />

          {/* Team routes with access codes (PUBLIC) */}
          <Route path="/:eventCode/:teamCode" element={<TeamPageWrapper />} />

          {/* 404 catch-all */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
